import React, { useEffect, useMemo, useRef, useState } from "react";
import type { GetProps } from "antd";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { ChannelEnum } from "../constants/enums";
import { useLocation } from "react-router-dom";
import { ChannelKeyMap } from "../constants/labels";

const { Title, Text } = Typography;
const { TextArea } = Input;

type TemplateStatus = "Active" | "Inactive";

type FormValues = {
  status: TemplateStatus;
  name: string;
  content: string;
};

const VARIABLE_TOKENS: string[] = [
  "{FirstName}",
  "{FullName}",
  "{BrandName}",
  "{Discount}",
  "{VoucherType}",
  "{VoucherName}",
  "{VoucherExpiry}",
  "{OnlineShop}",
  "{MemberPortal}",
];

const MAX_PER_SMS = 160;

export default function CreateMessageTemplates() {
  const [form] = Form.useForm<FormValues>();
  const [status, setStatus] = useState<TemplateStatus>("Active");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedCampaignChannel, setSelectedCampaignChannel] =
    useState<ChannelEnum | null>(null);
  const textAreaRef = useRef<GetProps<typeof TextArea>["ref"]>(null);
  const location = useLocation();

  const charCount = content.length;
  const smsParts = Math.max(1, Math.ceil(charCount / MAX_PER_SMS));

  const tooltip = (
    <div style={{ maxWidth: 280 }}>
      <Text type="secondary">
        {`Personalize your message with variables. These will be replaced with real data when the campaign is sent. e.g: Hi {FirstName}, enjoy your voucher from {BrandName}!`}
      </Text>
    </div>
  );

  const insertTokenAtCursor = (token: string) => {
    const el = (textAreaRef as any).current?.resizableTextArea?.textArea as
      | HTMLTextAreaElement
      | undefined;
    if (!el) {
      setContent((prev) => prev + token);
      form.setFieldValue(
        "content",
        (form.getFieldValue("content") || "") + token
      );
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const current = content;
    const next = current.slice(0, start) + token + current.slice(end);
    setContent(next);
    form.setFieldValue("content", next);
    // move caret
    const caret = start + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const onFinish = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await new Promise((r) => setTimeout(r, 500));
      message.success("Template created");
    } catch (e) {
      message.error("Failed to create template");
    } finally {
      setSubmitting(false);
    }
  };
  const isChannelEnum = (v: unknown): v is ChannelEnum =>
    typeof v === "number" && v in ChannelEnum;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("channel");
    const parsed = raw ? parseInt(raw, 10) : NaN;

    if (isChannelEnum(parsed)) {
      setSelectedCampaignChannel(parsed);
    } else {
      setSelectedCampaignChannel(null);
    }
  }, [location.search]);

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card
            title={
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={5} style={{ margin: 0 }}>
                    Create{" "}
                    {selectedCampaignChannel
                      ? ChannelKeyMap[selectedCampaignChannel]
                      : ""}{" "}
                    Template
                  </Title>
                </Col>
                <Col>
                  <Button onClick={() => window.history.back()}>Back</Button>
                </Col>
              </Row>
            }
          >
            <Form
              form={form}
              layout="horizontal"
              labelAlign="left"
              labelCol={{ xs: { span: 24 }, md: { span: 6 } }}
              wrapperCol={{ xs: { span: 24 }, md: { span: 18 } }}
              initialValues={{ status: status, name: "", content: "" }}
              onFinish={onFinish}
            >
              <Row gutter={[16, 0]}>
                <Col xs={24} md={24}>
                  <Form.Item
                    label="Template Status"
                    name="status"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[{ value: "Active" }, { value: "Inactive" }]}
                      onChange={(v) => setStatus(v)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={24}>
                  <Form.Item
                    label="Template Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a template name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter Template Name" maxLength={255} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 8]}>
                <Col xs={24} md={24}>
                  <Form.Item
                    label={
                      <Space>
                        Message Content
                        <Tooltip title={tooltip} placement="right">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    name="content"
                    rules={[
                      {
                        required: true,
                        message: "Please enter message content",
                      },
                    ]}
                  >
                    <TextArea
                      ref={textAreaRef as any}
                      autoSize={{ minRows: 4, maxRows: 8 }}
                      maxLength={1000}
                      value={content}
                      placeholder="Enter message content... (Max 160 characters per SMS; can include dynamic tags)"
                      onChange={(e) => setContent(e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Space>
                        Message Variables
                        <Tooltip title={tooltip} placement="right">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    className="label-filter"
                    name="content"
                  >
                    <Space wrap>
                      {VARIABLE_TOKENS.map((t) => (
                        <Button
                          key={t}
                          size="small"
                          onClick={() => insertTokenAtCursor(t)}
                        >
                          {t}
                        </Button>
                      ))}
                    </Space>
                  </Form.Item>
                </Col>

                <Col xs={24} md={24}>
                  <Form.Item label="Preview Message" className="label-filter">
                    <Card>
                      <TextArea
                        value={content}
                        readOnly
                        autoSize={{ minRows: 8 }}
                        placeholder="Your SMS Message will appear here..."
                      />
                      <Row justify="space-between" style={{ marginTop: 8 }}>
                        <Col>
                          <Text type="secondary">
                            Characters used: {charCount} / {MAX_PER_SMS}
                          </Text>
                        </Col>
                        <Col>
                          <Text type="secondary">Total SMS: {smsParts}</Text>
                        </Col>
                      </Row>
                    </Card>
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="end" gutter={8}>
                <Col>
                  <Button onClick={() => window.history.back()}>Cancel</Button>
                </Col>
                <Col>
                  <Button type="primary" htmlType="submit" loading={submitting}>
                    Submit
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
